import { defineStore } from "pinia";
import App from "@bloglovin/inspire-engine/App";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";

// Session-only, per-DynamicComponent undo/redo for the builder. Units come from
// the engine's capture sink (see dynamic-component-editor registerUndoCaptureSink):
// each unit is { records, selectionBefore, selectionAfter } where records are the
// coalesced {entityType, pk, op: created|updated|deleted, beforeFields, afterFields}.
// Undo/redo apply the inverse/forward op-set through ONE ApplyEntityDelta event
// (plain triggerEvent — no batch, so it never opens a capture window and can't
// re-capture itself; the same unit just moves between the stacks). Not persisted.
const MAX_DEPTH = 100;

// Extract a parent element id from a record's field blob. The records come from
// the engine capture sink, whose field-data carries the client oneEntity FK
// shape (parentElement = EntityReference { entityType, pk:{ id } }). Tolerate the
// flat { id } and a bare id too. Null when none. Without `pk.id` here every
// record buckets as a root and parents-first ordering collapses for nested trees
// (Wrap, paste, container subtrees).
function parentIdFromFields(fields) {
  const parent = fields?.parentElement;
  if (parent == null) {
    return null;
  }
  if (typeof parent === "object") {
    return String(parent.pk?.id ?? parent.id ?? "");
  }
  return String(parent);
}

// Order an op-set so referential integrity holds: creates parents-first (a child
// can't be inserted before its parent's row exists), deletes children-first.
// Updates are order-independent. Records reference elements by their original id.
function sortForApply(ops) {
  const creates = ops.filter(op => op.op === "create");
  const updates = ops.filter(op => op.op === "update");
  const deletes = ops.filter(op => op.op === "delete");

  const createByPk = new Map(creates.map(op => [String(op.pk.id), op]));
  const orderedCreates = [];
  const seen = new Set();
  const visitCreate = op => {
    const key = String(op.pk.id);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    const parentId = parentIdFromFields(op.fields);
    const parentOp = parentId != null ? createByPk.get(parentId) : null;
    if (parentOp) {
      visitCreate(parentOp); // emit the parent before this child
    }
    orderedCreates.push(op);
  };
  creates.forEach(visitCreate);

  // Children-first deletes: reverse of a parents-first ordering (by the captured
  // post-state parent, since a delete op carries no fields).
  const deleteByPk = new Map(deletes.map(op => [String(op.pk.id), op]));
  const orderedDeletesParentFirst = [];
  const seenDel = new Set();
  const visitDelete = op => {
    const key = String(op.pk.id);
    if (seenDel.has(key)) {
      return;
    }
    seenDel.add(key);
    const parentId = parentIdFromFields(op.afterParentFields);
    const parentOp = parentId != null ? deleteByPk.get(parentId) : null;
    if (parentOp) {
      visitDelete(parentOp);
    }
    orderedDeletesParentFirst.push(op);
  };
  deletes.forEach(visitDelete);

  return [...orderedCreates, ...updates, ...orderedDeletesParentFirst.reverse()];
}

// For an "updated" record, the captured before/after are FULL field-data blobs.
// Replaying the whole blob would rewrite fields the op never touched — including
// oneEntity FKs (e.g. a DynamicComponent's currentVersion, which the off-path
// snapshot writer advances independently) — clobbering them and tripping the
// engine when an unrelated FK happens to be null. So an update only carries the
// keys that actually changed, valued from the chosen side.
function changedFields(beforeFields, afterFields, side) {
  const before = beforeFields ?? {};
  const after = afterFields ?? {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const out = {};
  keys.forEach(key => {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      out[key] = side === "before" ? before[key] : after[key];
    }
  });
  return out;
}

// Map a captured record to the op that UNDOES it.
function inverseOp(record) {
  if (record.op === "created") {
    return { entityType: record.entityType, pk: record.pk, op: "delete", afterParentFields: record.afterFields };
  }
  if (record.op === "deleted") {
    return { entityType: record.entityType, pk: record.pk, op: "create", fields: record.beforeFields };
  }
  return {
    entityType: record.entityType,
    pk: record.pk,
    op: "update",
    fields: changedFields(record.beforeFields, record.afterFields, "before"),
  };
}

// Map a captured record to the op that RE-APPLIES it (redo).
function forwardOp(record) {
  if (record.op === "created") {
    return { entityType: record.entityType, pk: record.pk, op: "create", fields: record.afterFields };
  }
  if (record.op === "deleted") {
    return { entityType: record.entityType, pk: record.pk, op: "delete", afterParentFields: record.beforeFields };
  }
  return {
    entityType: record.entityType,
    pk: record.pk,
    op: "update",
    fields: changedFields(record.beforeFields, record.afterFields, "after"),
  };
}

// Strip the sort-only helper field before sending to the engine.
function toWireOps(ops) {
  return ops.map(op => {
    const wire = { ...op };
    delete wire.afterParentFields;
    return wire;
  });
}

// The capture records each entity under the pk it had AT OP TIME — a
// freshly-created entity is captured under its TEMP pk. By undo/redo time the
// in-flight sync has reconciled it to a real id; the client holds that temp→real
// map, but the server (a fresh request) does NOT. So resolve every op pk AND
// every oneEntity FK ref in the fields to its real id before sending, or the
// engine would update/delete a row id that no longer exists (the temp id) and
// 500 / leave a duplicate behind.
function resolveEntityRefTempPk(app, value) {
  if (value && typeof value === "object" && value.entityType && value.pk) {
    const realPk = app.resolveTempPk(value.entityType, value.pk);
    return realPk ? { ...value, pk: realPk } : value;
  }
  return value;
}

function resolveOpTempPks(app, op) {
  const resolved = { ...op, pk: app.resolveTempPk(op.entityType, op.pk) ?? op.pk };
  if (op.fields) {
    const fields = {};
    Object.keys(op.fields).forEach(key => {
      fields[key] = resolveEntityRefTempPk(app, op.fields[key]);
    });
    resolved.fields = fields;
  }
  return resolved;
}

// Swap any oneEntity FK ref in a captured field blob that points at the resolved
// temp pk onto the real pk (e.g. a child's parentElement that referenced its
// freshly-created parent's temp id).
function rewriteFkRefsInFields(fields, entityType, tempPkString, realPk) {
  if (!fields) {
    return fields;
  }
  let rewritten = null;
  Object.keys(fields).forEach(key => {
    const value = fields[key];
    if (value && typeof value === "object" && value.entityType === entityType
      && App.getPkString(value.pk ?? {}) === tempPkString) {
      rewritten = rewritten ?? { ...fields };
      rewritten[key] = { ...value, pk: { ...realPk } };
    }
  });
  return rewritten ?? fields;
}

// Rewrite a captured record's own pk and any FK refs in its before/after blobs
// from a resolved temp pk to the real pk.
function rewriteRecordTempPk(record, entityType, tempPkString, realPk) {
  let next = record;
  if (record.entityType === entityType && App.getPkString(record.pk ?? {}) === tempPkString) {
    next = { ...next, pk: { ...realPk } };
  }
  const before = rewriteFkRefsInFields(next.beforeFields, entityType, tempPkString, realPk);
  const after = rewriteFkRefsInFields(next.afterFields, entityType, tempPkString, realPk);
  if (before !== next.beforeFields || after !== next.afterFields) {
    next = { ...next, beforeFields: before, afterFields: after };
  }
  return next;
}

const useBuilderUndoStore = defineStore("builder-undo-store", {
  state: () => ({
    undoStacks: {}, // dcId -> unit[]
    redoStacks: {}, // dcId -> unit[]
  }),
  getters: {
    canUndo: state => dcId => (state.undoStacks[dcId]?.length ?? 0) > 0,
    canRedo: state => dcId => (state.redoStacks[dcId]?.length ?? 0) > 0,
  },
  actions: {
    // Record a fresh user op. Clears the redo stack (new edit = new branch).
    pushUnit(dcId, unit) {
      if (!dcId || !unit?.records?.length) {
        return;
      }
      const stack = this.undoStacks[dcId] ?? (this.undoStacks[dcId] = []);
      stack.push(unit);
      if (stack.length > MAX_DEPTH) {
        stack.shift();
      }
      this.redoStacks[dcId] = [];
    },
    undo(dcId) {
      const unit = this.undoStacks[dcId]?.pop();
      if (!unit) {
        return false;
      }
      this.applyOps(unit.records.map(inverseOp), unit.selectionBefore);
      (this.redoStacks[dcId] ?? (this.redoStacks[dcId] = [])).push(unit);
      return true;
    },
    redo(dcId) {
      const unit = this.redoStacks[dcId]?.pop();
      if (!unit) {
        return false;
      }
      this.applyOps(unit.records.map(forwardOp), unit.selectionAfter);
      (this.undoStacks[dcId] ?? (this.undoStacks[dcId] = [])).push(unit);
      return true;
    },
    // Fire the op-set as ONE ApplyEntityDelta event (plain triggerEvent: no
    // capture window opens, so undo/redo never re-capture). Then restore the
    // selection, filtered to ids that still resolve.
    applyOps(ops, selection) {
      const app = App.get("CreatorStudio");
      // Sort while pks are still temp-consistent (parent ref ↔ parent op pk match),
      // THEN resolve temp→real so the wire ops carry ids the server can find.
      const wireOps = toWireOps(sortForApply(ops)).map(op => resolveOpTempPks(app, op));
      // Undo/redo restoring `options` must beat any in-flight echo of the reverted
      // edit — stamp a fresh optionsVersion so the merge guard keeps it.
      wireOps.forEach(op => {
        if (op.entityType === "Element" && op.op === "update" && op.fields && "options" in op.fields) {
          const current = DynamicElement.get(String(op.pk?.id))?.element?.optionsVersion ?? 0;
          op.fields.optionsVersion = current + 1;
        }
      });
      app.triggerEvent("ApplyEntityDelta", { records: JSON.stringify(wireOps) });
      const editor = useDynamicComponentEditorStore();
      editor.currentlySelectedElementIds = (selection ?? []).filter(id => DynamicElement.get(String(id).split(":")[0]));
    },
    // Rewrite a resolved temp→real pk across every stacked unit. Capture records
    // an entity under the TEMP pk it had at op time; when the in-flight sync
    // reconciles it, this swings the stored records (and their FK refs) onto the
    // real id so a later undo/redo targets a row the server can find. Done at
    // reconcile time (mapping guaranteed fresh) rather than only at apply time.
    reconcileTempPk(entityType, tempPkString, realPk) {
      const rewriteUnit = unit => ({
        ...unit,
        records: unit.records.map(record => rewriteRecordTempPk(record, entityType, tempPkString, realPk)),
      });
      Object.keys(this.undoStacks).forEach(dcId => {
        this.undoStacks[dcId] = this.undoStacks[dcId].map(rewriteUnit);
      });
      Object.keys(this.redoStacks).forEach(dcId => {
        this.redoStacks[dcId] = this.redoStacks[dcId].map(rewriteUnit);
      });
    },
    // Drop a DC's history (e.g. after a maintainer version rollback supersedes it).
    clearForDynamicComponent(dcId) {
      delete this.undoStacks[dcId];
      delete this.redoStacks[dcId];
    },
  },
});

export default useBuilderUndoStore;
